import { Injectable, OnDestroy, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, Observable, debounceTime, Subscription, timer } from 'rxjs';
import { QueueService } from '@core/services/queue.service';
import { NetworkStatusService } from '@core/services/network-status.service';
import { LoggerService } from '@shared/services/logger.service';
import { SyncService } from './sync.service';

@Injectable({ providedIn: 'root' })
export class AutoSyncService implements OnDestroy {
  #queueService = inject(QueueService);
  #networkStatusService = inject(NetworkStatusService);
  #syncService = inject(SyncService);
  #logger = inject(LoggerService);

  readonly #DEBOUNCE_TIME_MS = 2000; // 2 seconds
  readonly #SYNC_RETRY_DELAY_FOR_IN_PROGRESS_SYNC_MS = 5000; // 5 seconds
  readonly #INITIAL_RETRY_DELAY_FOR_FAILED_SYNC_MS = 30000; // 30 seconds
  readonly #MAX_RETRY_DELAY_FOR_FAILED_SYNC_MS = 1800000; // 30 minutes
  readonly #BACKOFF_MULTIPLIER = 2;
  #isStarted = false;
  #retrySubscription?: Subscription;
  #autoSyncSubscription?: Subscription;
  #currentRetryForFailedSyncAttempt = 0;
  #hasConnectivity$: Observable<boolean>;

  public constructor() {
    this.#hasConnectivity$ = toObservable(this.#networkStatusService.hasConnectivity);
  }

  public start(): void {
    if (this.#isStarted) {
      this.#logger.warn('AutoSyncService: Already started.');
      return;
    }

    this.#isStarted = true;
    this.#logger.info('AutoSyncService: Starting auto-sync monitoring.');

    this.#autoSyncSubscription = combineLatest([
      this.#hasConnectivity$,
      this.#queueService.tasksWithActions$.pipe(debounceTime(this.#DEBOUNCE_TIME_MS)),
    ]).subscribe(([hasConnectivity, tasks]) => {
      if (hasConnectivity && tasks.length > 0) {
        this.#logger.info('AutoSyncService: Conditions met, attempting sync.');
        this.attemptSync();
      }
    });
  }

  public ngOnDestroy(): void {
    this.#retrySubscription?.unsubscribe();
    this.#autoSyncSubscription?.unsubscribe();
    this.#currentRetryForFailedSyncAttempt = 0;
  }

  private async attemptSync(): Promise<void> {
    const result = await this.#syncService.startSync();

    this.stopRetryMechanism();
    if (result.status === 'InProgress') {
      this.scheduleRetryForSyncInProgress();
      return;
    }
    if (result.status === 'CompletedWithErrors') {
      this.scheduleRetryForFailedSync();
      return;
    }
    this.#currentRetryForFailedSyncAttempt = 0;
  }

  private scheduleRetryForSyncInProgress(): void {
    this.#logger.info('AutoSyncService: Sync in progress, scheduling retry.');

    this.#retrySubscription = timer(this.#SYNC_RETRY_DELAY_FOR_IN_PROGRESS_SYNC_MS).subscribe(() => {
      this.#logger.info('AutoSyncService: Retrying sync after delay.');
      this.attemptSync();
    });
  }

  private scheduleRetryForFailedSync(): void {
    this.#logger.info('AutoSyncService: Sync failed, scheduling retry.');

    const delay = this.calculateBackoffDelay();
    this.#retrySubscription = timer(delay).subscribe(() => {
      this.#currentRetryForFailedSyncAttempt++;
      this.#logger.info(
        `AutoSyncService: Retrying sync after delay because of failed sync (attempt ${this.#currentRetryForFailedSyncAttempt}).`,
      );
      this.attemptSync();
    });
  }

  private calculateBackoffDelay(): number {
    const exponentialDelay =
      this.#INITIAL_RETRY_DELAY_FOR_FAILED_SYNC_MS * Math.pow(this.#BACKOFF_MULTIPLIER, this.#currentRetryForFailedSyncAttempt);

    return Math.min(exponentialDelay, this.#MAX_RETRY_DELAY_FOR_FAILED_SYNC_MS);
  }

  private stopRetryMechanism(): void {
    if (this.#retrySubscription) {
      this.#retrySubscription.unsubscribe();
      this.#retrySubscription = undefined;
    }
  }
}
